if (!customElements.get('mm-decor-quiz')) {
  customElements.define('mm-decor-quiz', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      try {
        this.config = JSON.parse(this.querySelector('[data-mm-quiz-config]').textContent);
        this.products = JSON.parse(this.querySelector('[data-mm-quiz-products]').textContent).filter(p => p.available && Array.isArray(p.variants) && p.variants.length);
      } catch (_) { return; }
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.dialog = this.querySelector('dialog');
      this.question = this.querySelector('[data-mm-quiz-question]');
      this.results = this.querySelector('[data-mm-quiz-results]');
      this.next = this.querySelector('[data-mm-quiz-next]');
      this.back = this.querySelector('[data-mm-quiz-back]');
      this.step = 0;
      this.answers = {};
      document.addEventListener('click', event => {
        const trigger = event.target.closest('[data-mm-open-quiz], a[href="#mm-quiz"]');
        if (!trigger || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0) return;
        event.preventDefault();
        this.lastFocus = trigger;
        this.render();
        if (!this.dialog.open) this.dialog.showModal();
        document.documentElement.classList.add('mm-quiz-open');
        this.focusQuestion();
      }, { signal });
      this.querySelector('[data-mm-quiz-close]').addEventListener('click', () => this.dialog.close(), { signal });
      this.dialog.addEventListener('close', () => { document.documentElement.classList.remove('mm-quiz-open'); this.lastFocus?.focus(); }, { signal });
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, { signal });
      this.question.addEventListener('change', event => {
        const input = event.target.closest('input[data-mm-choice]');
        if (!input) return;
        const group = this.config.questions[this.step].group;
        this.selectAnswer(group, input.value, input.checked);
        this.next.disabled = !this.hasAnswer(group);
        if (this.isMultiple(group)) {
          this.question.querySelectorAll('input[data-mm-choice]').forEach(el => {
            el.checked = this.selectedKeys(group).includes(el.value);
          });
        }
      }, { signal });
      this.next.addEventListener('click', () => {
        if (this.step >= this.config.questions.length) { this.dialog.close(); return; }
        if (!this.hasAnswer(this.config.questions[this.step].group)) return;
        this.step += 1;
        this.render();
        this.focusQuestion();
      }, { signal });
      this.back.addEventListener('click', () => {
        if (this.step >= this.config.questions.length) { this.step = 0; this.answers = {}; }
        else this.step = Math.max(0, this.step - 1);
        this.render();
        this.focusQuestion();
      }, { signal });
    }
    normalize(value) { return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase(); }
    isMultiple(group) { return group === 'color' || group === 'family'; }
    selectedKeys(group) {
      const answer = this.answers[group];
      return Array.isArray(answer) ? answer : answer ? [answer] : [];
    }
    hasAnswer(group) { return this.selectedKeys(group).length > 0; }
    choices(group) { return this.config.choices.filter(c => c.group === group && this.selectedKeys(group).includes(c.key)); }
    choice(group) { return this.choices(group)[0]; }
    selectAnswer(group, key, checked) {
      if (!this.isMultiple(group)) { this.answers[group] = key; return; }
      const option = this.config.choices.find(c => c.group === group && c.key === key);
      let keys = this.selectedKeys(group).filter(k => k !== key);
      if (checked) {
        keys = this.tokens(option).length ? keys.filter(k => this.tokens(this.config.choices.find(c => c.group === group && c.key === k)).length) : [];
        keys.push(key);
      }
      this.answers[group] = keys;
    }
    groupMatches(product, group) {
      const choices = this.choices(group);
      return !choices.length || choices.some(choice => this.matches(product, choice));
    }
    tokens(choice) { return (choice?.tags || []).map(t => this.normalize(t)).filter(Boolean); }
    matches(product, choice) {
      const tokens = this.tokens(choice);
      if (!tokens.length) return true;
      if (choice.group === 'finish') return product.variants.some(v => tokens.includes(this.normalize(v.finish)));
      const tags = (product.tags || []).map(t => this.normalize(t));
      return tokens.some(t => tags.includes(t));
    }
    node(tag, className, text) {
      const el = document.createElement(tag);
      if (className) el.className = className;
      if (text !== undefined) el.textContent = text;
      return el;
    }
    render() {
      const total = this.config.questions.length;
      const done = this.step >= total;
      this.question.hidden = done;
      this.results.hidden = !done;
      this.querySelector('[data-mm-quiz-progress]').hidden = done;
      this.back.hidden = !done && this.step === 0;
      this.back.textContent = done ? this.config.copy.restart : this.config.copy.back;
      this.next.textContent = done ? this.config.copy.browseButton : this.step === total - 1 ? this.config.copy.showResults : this.config.copy.next;
      this.next.disabled = !done && !this.hasAnswer(this.config.questions[this.step].group);
      if (done) { this.renderResults(); return; }
      this.querySelector('[data-mm-quiz-step-label]').textContent = `${this.config.copy.progress} ${this.step + 1} / ${total}`;
      const track = this.querySelector('[role="progressbar"]');
      track.setAttribute('aria-valuemax', String(total));
      track.setAttribute('aria-valuenow', String(this.step + 1));
      this.querySelector('[data-mm-quiz-progress-bar]').style.width = `${(this.step + 1) / total * 100}%`;
      const q = this.config.questions[this.step];
      const fieldset = this.node('fieldset');
      const legend = this.node('legend', '', q.label);
      legend.tabIndex = -1;
      fieldset.append(legend);
      if (this.isMultiple(q.group)) fieldset.append(this.node('p', 'mm-quiz__hint', this.config.copy.multipleHint));
      if (q.group === 'finish') fieldset.append(this.node('p', 'mm-quiz__hint', this.config.copy.finishIntro));
      const choices = this.node('div', 'mm-quiz__choices');
      const options = this.config.choices.filter(c => c.group === q.group && (!['color', 'family'].includes(q.group) || !this.tokens(c).length || this.products.some(p => this.matches(p, c))));
      options.forEach(choice => {
        const label = this.node('label', 'mm-quiz__choice');
        const input = this.node('input');
        input.type = this.isMultiple(q.group) ? 'checkbox' : 'radio';
        input.name = `${this.id}-${q.group}`;
        input.value = choice.key;
        input.dataset.mmChoice = '';
        input.checked = this.selectedKeys(q.group).includes(choice.key);
        label.append(input);
        if (q.group === 'color' && this.tokens(choice).length) {
          const swatch = this.node('span', 'mm-quiz__swatch');
          swatch.setAttribute('aria-hidden', 'true');
          swatch.style.backgroundColor = choice.swatch;
          label.append(swatch);
        }
        label.append(this.node('span', '', choice.label));
        choices.append(label);
      });
      fieldset.append(choices);
      this.question.replaceChildren(fieldset);
    }
    rankedProducts() {
      const finish = this.choice('finish');
      const candidates = this.products.filter(product => !finish || this.matches(product, finish)).map(product => {
        const weights = { room: 1, mood: 2, color: 5, family: 6 };
        const score = Object.entries(weights).reduce((sum, [group, weight]) => {
          const specific = this.choices(group).filter(c => this.tokens(c).length);
          return sum + (specific.some(c => this.matches(product, c)) ? weight : 0);
        }, 0);
        return { product, score };
      });
      const priorities = ['color', 'family'].filter(group => this.choices(group).some(c => this.tokens(c).length));
      const exact = candidates.filter(item => priorities.every(group => this.groupMatches(item.product, group)));
      const pool = exact.length ? exact : candidates;
      pool.sort((a, b) => b.score - a.score || a.product.title.localeCompare(b.product.title, 'fr'));
      return { items: pool.slice(0, Math.max(1, Number(this.dataset.resultCount) || 4)), alternative: priorities.length > 0 && exact.length === 0 };
    }
    productUrl(product) {
      const url = new URL(product.url, document.baseURI);
      if (url.origin !== location.origin) return this.config.browseUrl;
      const finish = this.choice('finish');
      const tokens = this.tokens(finish);
      if (tokens.length) {
        const variant = product.variants.find(v => tokens.includes(this.normalize(v.finish)));
        if (variant) url.searchParams.set('variant', String(variant.id));
      }
      return `${url.pathname}${url.search}${url.hash}`;
    }
    renderResults() {
      const ranked = this.rankedProducts();
      const heading = this.node('h3', '', this.config.copy.resultHeading);
      heading.tabIndex = -1;
      const intro = this.node('p', 'mm-quiz__result-intro', ranked.items.length ? (ranked.alternative ? this.config.copy.alternativeIntro : this.config.copy.resultIntro) : this.config.copy.emptyText);
      const grid = this.node('div', 'mm-quiz__grid');
      ranked.items.forEach(({ product }) => {
        const card = this.node('article', 'mm-quiz__card');
        const href = this.productUrl(product);
        if (product.image) {
          const photoLink = this.node('a');
          photoLink.href = href;
          photoLink.tabIndex = -1;
          photoLink.setAttribute('aria-hidden', 'true');
          const image = this.node('img', 'mm-quiz__image');
          image.src = product.image;
          image.alt = '';
          image.loading = 'lazy';
          image.width = 720;
          image.height = 540;
          photoLink.append(image);
          card.append(photoLink);
        }
        const body = this.node('div', 'mm-quiz__card-body');
        body.append(this.node('h4', '', product.title));
        const reasons = this.node('div', 'mm-quiz__reasons');
        ['color', 'family', 'finish'].forEach(group => {
          this.choices(group).forEach(choice => {
            if (this.tokens(choice).length && this.matches(product, choice)) reasons.append(this.node('span', 'mm-quiz__reason', choice.label));
          });
        });
        if (reasons.childElementCount) body.append(reasons);
        const link = this.node('a', 'mm-quiz__product-link', this.config.copy.productButton);
        link.href = href;
        body.append(link);
        card.append(body);
        grid.append(card);
      });
      this.results.replaceChildren(heading, intro, grid);
    }
    focusQuestion() {
      requestAnimationFrame(() => {
        (this.step >= this.config.questions.length ? this.results.querySelector('h3') : this.question.querySelector('legend'))?.focus({ preventScroll: true });
        this.dialog.scrollTop = 0;
      });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      if (this.dialog?.open) this.dialog.close();
      document.documentElement.classList.remove('mm-quiz-open');
    }
  });
}
